import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Grid,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Alert,
  Snackbar,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GET, POST } from "APIclient";
import properties from "constants/ApplicationConstants";
import { useLocation, useNavigate } from "react-router-dom";
import MDButton from "components/MDButton";

const AddAsset = () => {
  const navigate = useNavigate();
  const [components, setComponents] = useState([]);
  const [componentId, setComponentId] = useState("");
  const [assetNo, setAssetNo] = useState("");
  const [assetName, setAssetName] = useState("");
  const [description, setDescription] = useState("");
  const [assetBy, setAssetBy] = useState("");
  const [project, setProject] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [errors, setErrors] = useState({});
  const [loadingComponents, setLoadingComponents] = useState(true);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [open, setOpen] = React.useState(false);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [customProjectName, setCustomProjectName] = useState("");
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [customClientName, setCustomClientName] = useState("");


  const fetchProjects = async () => {
    try {
      const response = await GET("admin/project/index");
      

      const list = response.data?.data?.paginatedData || [];
      const mappedList = list.map((p) => ({
        id: p.projectId,
        projectName: p.projectName,
      }));

      setProjects(mappedList);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await GET("admin/client-list?page=0&size=1000");
      

      const list = response.data?.data?.paginatedData || [];
      const mappedList = list.map((c) => ({
        id: c.clientId,
        clientName: c.clientName,
      }));

      setClients(mappedList);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        const response = await GET("it-dept/components");
        

        const list = response.data?.data || [];
        const mappedList = list.map((c) => ({
          id: c.componentId,
          componentName: c.componentName,
        }));

        setComponents(mappedList);
      } catch (error) {
        console.error("Error fetching components:", error);
        setComponents([]);
      } finally {
        setLoadingComponents(false);
      }
    };

    fetchComponents();
    fetchProjects();
    fetchClients();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!componentId) newErrors.componentId = "Component is required";
    if (!assetNo) newErrors.assetNo = "Asset Number is required";
    if (!assetName) newErrors.assetName = "Asset Main Description is required";
    if (!description) newErrors.description = "Description is required";
    if (!assetBy) newErrors.assetBy = "Asset By is required";
    if (assetBy === "Other" && !customClientName.trim()) newErrors.customClientName = "Custom client name is required";
    if (!project) newErrors.project = "Project is required";
    if (project === "Other" && !customProjectName.trim()) newErrors.customProjectName = "Custom project name is required";
    if (!purchaseDate) newErrors.purchaseDate = "Purchase Date is required";



    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      componentId: Number(componentId),
      assetNo,
      assetName,
      description,
      assetBy: assetBy === "Other" ? customClientName.trim() : assetBy,
      project: project === "Other" ? customProjectName.trim() : project,
      purchaseDate,
    };

    try {
      const response = await POST("it-dept/assets/add", payload);

      if (response.data?.status === "CREATED" || response.data?.statusCode === 201 || response.data?.success) {
        setSnakColor("success");
        setSnakMessage("Asset added successfully");
        setOpen(true);

        setComponentId("");
        setAssetNo("");
        setAssetName("");
        setDescription("");
        setAssetBy("");
        setProject("");
        setCustomProjectName("");
        setPurchaseDate("");

        setCustomClientName("");
        setErrors({});
        setTimeout(() => {
          navigate("/assetList");
        }, 1500);
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      if (statusCode === 409) {
        setErrors({ assetNo: "Asset number already exists" });
        setSnakColor("error");
        setSnakMessage("Asset number already exists.");
      } else if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage("Bad request. Please check your inputs.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(error?.message || "Unexpected error occurred.");
      }
      setOpen(true);
    }
  };

  const isFormValid = () => {
    const hasAllRequired =
      componentId &&
      assetNo &&
      assetName &&
      description &&
      assetBy &&
      project &&
      purchaseDate &&
      (assetBy !== "Other" || (assetBy === "Other" && customClientName.trim())) &&
      (project !== "Other" || (project === "Other" && customProjectName.trim()));

    return hasAllRequired;
  };



  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ display: "flex", minHeight: "100vh", flexDirection: "column", backgroundColor: "#f4f6f8", padding: "1rem" }}>
        <Grid container spacing={1.5} sx={{ backgroundColor: "#fff", borderRadius: "8px", padding: "1rem", boxShadow: 3 }}>
          <Grid item xs={12}><h4>Add New Asset</h4></Grid>


          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal" error={!!errors.componentId}>
              <InputLabel id="component-label">Component <span style={{ color: properties.colors.Red }}>*</span></InputLabel>
              <Select
                labelId="component-label"
                value={componentId}
                onChange={(e) => setComponentId(e.target.value)}
                sx={{ height: "45px" }}
              >
                {loadingComponents ? (
                  <MenuItem disabled>Loading components...</MenuItem>
                ) : components.length === 0 ? (
                  <MenuItem disabled>No components available</MenuItem>
                ) : (
                  components.map((comp) => (
                    <MenuItem key={comp.id} value={comp.id.toString()}>
                      {comp.componentName}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.componentId && <FormHelperText>{errors.componentId}</FormHelperText>}
            </FormControl>
          </Grid>


          <Grid item xs={12} sm={6}>
            <TextField
              label={
                <>
                  Asset No <span style={{ color: properties.colors.Red }}>*</span>
                </>
              }
              fullwidth
              value={assetNo}
              onChange={(e) => setAssetNo(e.target.value)}
              error={!!errors.assetNo}
              helperText={errors.assetNo || ""}
              placeholder="YYYYMMDD/number"
              margin="normal"
            />
          </Grid>


          <Grid item xs={12} sm={6}>
            <TextField
              label={<>Asset Main Description <span style={{ color: properties.colors.Red }}>*</span></>} fullWidth multiline rows={3} value={assetName} onChange={(e) => setAssetName(e.target.value)} error={!!errors.assetName} helperText={errors.assetName || ""} margin="normal" />
          </Grid>


          <Grid item xs={12} sm={6}>
            <TextField label={<>Sub Description <span style={{ color: properties.colors.Red }}>*</span></>} fullWidth multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} error={!!errors.description} helperText={errors.description || ""} margin="normal" />
          </Grid>


          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal" error={!!errors.assetBy}>
              <InputLabel id="assetBy-label">Asset By <span style={{ color: properties.colors.Red }}>*</span></InputLabel>
              <Select
                labelId="assetBy-label"
                value={assetBy}
                onChange={(e) => {
                  setAssetBy(e.target.value);
                  if (e.target.value !== "Other") {
                    setCustomClientName("");
                  }
                }}
                sx={{ height: "45px" }}
              >
                {loadingClients ? (
                  <MenuItem disabled>Loading clients...</MenuItem>
                ) : clients.length === 0 ? (
                  <MenuItem disabled>No clients available</MenuItem>
                ) : (
                  clients.map((client) => (
                    <MenuItem key={client.id} value={client.clientName}>
                      {client.clientName}
                    </MenuItem>
                  ))
                )}
                {/* {!loadingClients && <MenuItem value="Other">Other</MenuItem>} */}
              </Select>
              {errors.assetBy && <FormHelperText>{errors.assetBy}</FormHelperText>}
            </FormControl>
          </Grid>


          {/* {assetBy === "Other" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Custom Client Name"
                fullWidth
                value={customClientName}
                onChange={(e) => setCustomClientName(e.target.value)}
                error={!!errors.customClientName}
                helperText={errors.customClientName || ""}
                margin="normal"
              />
            </Grid>
          )} */}


          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal" error={!!errors.project}>
              <InputLabel id="project-label">Project <span style={{ color: properties.colors.Red }}>*</span></InputLabel>
              <Select
                labelId="project-label"
                value={project}
                onChange={(e) => {
                  setProject(e.target.value);
                  if (e.target.value !== "Other") {
                    setCustomProjectName("");
                  }
                }}
                sx={{ height: "45px" }}
              >
                {loadingProjects ? (
                  <MenuItem disabled>Loading projects...</MenuItem>
                ) : projects.length === 0 ? (
                  <MenuItem disabled>No projects available</MenuItem>
                ) : (
                  projects.map((proj) => (
                    <MenuItem key={proj.id} value={proj.projectName}>
                      {proj.projectName}
                    </MenuItem>
                  ))
                )}
                {/* {!loadingProjects && <MenuItem value="Other">Other</MenuItem>} */}
              </Select>
              {errors.project && <FormHelperText>{errors.project}</FormHelperText>}
            </FormControl>
          </Grid>


          {/* {project === "Other" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Custom Project Name"
                fullWidth
                value={customProjectName}
                onChange={(e) => setCustomProjectName(e.target.value)}
                error={!!errors.customProjectName}
                helperText={errors.customProjectName || ""}
                margin="normal"
              />
            </Grid>
          )} */}


          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  format="DD/MM/YYYY"
                  sx={{ m: 1, width: "100%" }}
                  size="small"
                  label={
                    <span>
                      Purchase Date <span style={{ color: properties.colors.Red }}>*</span>
                    </span>
                  }
                  value={purchaseDate ? dayjs(purchaseDate) : null}
                  onChange={(date) => setPurchaseDate(date ? date.format("YYYY-MM-DD") : "")}
                  shouldDisableDate={(date) => {
                    const today = dayjs();
                    return date.isAfter(today, "day");
                  }}
                  slotProps={{
                    textField: {
                      error: !!errors.purchaseDate,
                      helperText: errors.purchaseDate || ""
                    }
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <MDButton variant="contained" color="info" onClick={handleSubmit} disabled={!isFormValid()}>Save</MDButton>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          autoHideDuration={1200}
          onClose={handleClose}
          severity={snakColor}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snakMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default AddAsset;
